import React from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models, Report } from 'powerbi-client';
import * as config from '../Config';
import './PowerBIReport.css';

import { ReactComponent as PlusSvg } from '../assets/plus.svg';
import { ReactComponent as MinusSvg } from '../assets/minus.svg';

const PowerBIReport: React.FC<{
  embedUrl: string;
  accessToken: string;
  reportRef: React.MutableRefObject<Report | null>;
  initPage?: number;
  onLoad?: () => void;
  onRender?: () => void;
  username: string;
}> = ({ embedUrl, accessToken, reportRef, initPage, onLoad, onRender, username }) => {
  const reportClass = 'report-container';

  const zoomIn = async () => {
    if (reportRef.current) {
      const currentZoom = await reportRef.current.getZoom();
      const newZoom = Math.min(currentZoom + 0.2, 2); // max zoom level 2
      await reportRef.current.setZoom(newZoom);
    }
  };

  const zoomOut = async () => {
    if (reportRef.current) {
      const currentZoom = await reportRef.current.getZoom();
      const newZoom = Math.max(currentZoom - 0.2, 0.3); // min zoom level 0.3
      await reportRef.current.setZoom(newZoom);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <div
          style={{
            flex: 1,
            width: '100%',
            borderRadius: '2.1vh',
            overflow: 'hidden',
            border: '0.2vh solid rgba(16, 29, 59, 0.2)',
          }}
        >
          <PowerBIEmbed
            embedConfig={{
              type: 'report',
              id: config.reportId,
              embedUrl: embedUrl,
              accessToken: accessToken,
              tokenType: models.TokenType.Aad,
              settings: {
                panes: {
                  filters: {
                    expanded: false,
                    visible: false,
                  },
                  pageNavigation: {
                    visible: false,
                  },
                },
                background: models.BackgroundType.Transparent,
              },
            }}
            eventHandlers={
              new Map([
                [
                  'loaded',
                  function () {
                    if (onLoad) {
                      onLoad();
                    }

                    if (initPage) {
                      let report = reportRef.current!;

                      report.getPages().then(pages => {
                        const page = pages[initPage];
                        if (page) {
                          page.setActive();
                        }
                      });
                    }
                  },
                ],
                [
                  'rendered',
                  function () {
                    if (onRender) {
                      onRender();
                    }
                  },
                ],
                [
                  'error',
                  function (event) {
                    console.error(event?.detail);
                  },
                ],
              ])
            }
            cssClassName={reportClass}
            getEmbeddedComponent={embeddedReport => {
              if (reportRef) {
                reportRef.current = embeddedReport as Report;
                reportRef.current = embeddedReport as Report;
              }
            }}
          />
        </div>
        <div style={{ height: '1.6vh' }} />
        <ZoomControls zoomOut={zoomOut} zoomIn={zoomIn} />
        <div style={{ height: '2.4vh' }} />
      </div>
    </>
  );
};

const ZoomControls: React.FC<{ zoomOut: () => void; zoomIn: () => void }> = ({ zoomOut, zoomIn }) => {
  return (
    <div style={styles.buttonContainer}>
      <button onClick={zoomOut} style={{ ...styles.button }} title="Zoom Out">
        <MinusSvg fill="#00AB9B" height={'2.4vh'} width={'2.4vh'} />
      </button>
      <div style={{ width: '1.6vh' }} />
      <button onClick={zoomIn} style={{ ...styles.button }} title="Zoom In">
        <PlusSvg fill="#00AB9B" height={'2.4vh'} width={'2.4vh'} />
      </button>
    </div>
  );
};

const styles = {
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    height: '4vh',
    width: '4vh',
    backgroundColor: 'transparent',
    border: '0.2vh solid rgba(0, 171, 155, 1)', // Light grey border for definition
    borderRadius: '50%',
    cursor: 'pointer',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export default PowerBIReport;
